import { useEffect, useState, useCallback, useMemo } from "react";
import {
  Box,
  Card,
  Typography,
  IconButton,
  TextField,
  Button,
  CircularProgress,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Switch,
  Chip,
  Divider,
  Checkbox,
  FormControlLabel,
  Collapse,
  Stack,
  Grid,
} from "@mui/material";
import { Delete, Edit, Close, Refresh, Add, ExpandMore, ExpandLess } from "@mui/icons-material";
import { alpha, styled, useTheme } from "@mui/material/styles";
import AppPaginationBar from "components/AppPaginationBar";
import apiService from "Network/apiService";
import { API_ENDPOINTS } from "Network/apiEndpoints";
import { useToast } from "OtherReusableFunctionality/ToastContext";
import { useDeleteConfirmation } from "contexts/DeleteConfirmationContext";
import { usePageTitle } from "contexts/PageTitleContext";
import { getAllNavigationItems } from "layouts/layout-parts/navigation";
import buttonConfig from "config/ConfigAccessButton"; // Import button config

const PageContainer = styled(Box)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const HeaderCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
  marginBottom: theme.spacing(2),
  display: "flex",
  alignItems: "center",
  justifyContent: "space-between",
  flexWrap: "wrap",
  gap: theme.spacing(2),
}));

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  fontSize: "0.875rem",
  padding: "12px 16px",
  "&.header": {
    fontWeight: 600,
    backgroundColor: theme.palette.grey[100],
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(even)": {
    backgroundColor: theme.palette.action.hover,
  },
  "&:hover": {
    backgroundColor: theme.palette.action.hover,
  },
}));

const TableCard = styled(Card)(({ theme }) => ({
  padding: theme.spacing(2),
}));

const Muted = styled(Typography)(({ theme }) => ({
  color: theme.palette.text.secondary,
}));

// Mapping between UI display names and API tab names
const TAB_NAME_MAPPING = {
  "App Order": "appOrders",
  "Agronomist": "talkToAgronomist",
  "Bulk Order": "bulkOrders",
  "Return & Cancel Request": "returnCancelRequest",
  "Quotation/Proforma Invoice": "quotation",
  "Product Management/Agri store": "agriStore",
  "Translation Management/Content": "groceries",
  "Post": "farmerForum",
  "Knowledge hub": "successStories",
  "Event Types": "eventType",
  "Crop types": "cropType",
  "Enquiry Form": "enquiry",
  "Field visit": "fieldVisit",
  "Category": "category",
  "Crop hub": "cropHub",
  "Disease Advisory": "diseaseAdvisory",
  "AI Chat Bot": "aiChatBot",
  "Users": "users",
  "User Types & Permissions": "permissions",
  "FAQ": "FAQs",
  "FAQs": "FAQs",
  "Dashboard": "dashboard",
  "Farmer Management": "farmerManagement",
  "Invoice": "invoice"
};

// Define button structure for each module (using exact navigation names)
const BUTTON_STRUCTURE = {
  "App Order": {
    "Placed Button": ["advanced_export", "update_status", "update_payment", "send_email"],
    "Confirmed Button": ["update_status", "update_payment",  "advanced_export", "send_email"],
    "Processing Button": ["advanced_export", "update_status", "update_payment", "send_email"],
    "Dispatch Button": ["advanced_export", "update_status", "update_payment", "send_email", "view_invoice", "view_delivery_challan"],
    "Delivered Button": ["advanced_export", "update_status", "update_payment", "send_email", "view_invoice", "view_delivery_challan"],
    "Cancelled Button": ["advanced_export", "update_status", "update_payment", "send_email", "view_invoice", "view_delivery_challan"],
    "Returned Button": ["advanced_export", "update_status", "update_payment", "send_email", "view_invoice", "view_delivery_challan"]
  },
  "Agronomist": {
    "Pending Button": ["update_status"],
    "Connected Button": ["update_status"]
    
  },
  "Bulk Order": {
    "Placed Button": ["advanced_export", "update_status", "update_payment", "send_email", "bulk_upload"],
    "Confirmed Button": ["advanced_export", "update_status", "update_payment", "send_email", "bulk_upload"],
    "Processing Button": ["advanced_export", "update_status", "update_payment", "send_email", "bulk_upload"],
    "Dispatch Button": ["advanced_export", "update_status", "update_payment", "send_email", "view_invoice", "view_delivery_challan", "bulk_upload"],
    "Delivered Button": ["advanced_export", "update_status", "update_payment", "send_email", "view_invoice", "view_delivery_challan", "bulk_upload"],
    "Cancelled Button": ["advanced_export", "update_status", "update_payment", "send_email", "view_invoice", "view_delivery_challan", "bulk_upload"],
    "Returned Button": ["advanced_export", "update_status", "update_payment", "send_email", "view_invoice", "view_delivery_challan", "bulk_upload", "view", "download"]
  },
  "Return & Cancel Request": {
    "cancel request button": ["accepted", "rejected"],
    "return request button": ["accepted", "rejected"]
  },
  "Quotation/Proforma Invoice": ["create_quotation_proforma", "view_quotation_pdf", "send_email", "view_proforma_invoice"],
  "Agri-store": ["add_product", "manage_companies_and_categories", "manage_image", "edit_product", "delete_product"],
  "Translated Content": ["add_translation", "edit", "delete"],
  "Posts": ["delete", "view_likes", "comment"],
  "Knowledge hub": ["manage_categories", "add_course", "edit", "delete", "open"],
  "Event Types": ["create_event", "update", "edit", "delete"],
  "Crop Types": ["create_crop", "update", "edit", "delete"],
  "Enquiry Form": ["enquiry_upload", "export_to_excel"],
  "Field Visit": ["export_to_excel", "view_image"],
  "Category": ["add_category", "edit", "delete"],
  "Crop Hub": ["add_crop", "view", "edit", "delete"],
  "Disease Advisory": {
    "Categories Button": ["add_category", "edit", "delete"],
    "Sub Categories Button": ["add_category", "edit", "delete"],
    "FAQs Button": ["add_faqs", "edit", "delete"]
  },
  "AI Chat Bot": ["add_questions", "edit", "delete"],
  "Users": ["add_user", "edit", "delete"],
  "Permissions": ["add_user_type", "edit_permission", "edit_user_type", "delete_user_type"],
  "FAQ": ["faq_upload", "add_faq", "edit_faq", "delete_faq"],
  "FAQs": ["faq_upload", "add_faq", "edit_faq", "delete_faq"]
};

const normalizeNavKey = (value = "") => value.trim().toLowerCase();

const getMatchingConfigKey = (config, navName) => {
  const exactKey = Object.keys(config).find((key) => normalizeNavKey(key) === normalizeNavKey(navName));
  return exactKey || navName;
};

// Helper functions
const moduleHasButtons = (navName) => {
  const hasButtons = BUTTON_STRUCTURE.hasOwnProperty(navName) || Object.keys(BUTTON_STRUCTURE).some((key) => normalizeNavKey(key) === normalizeNavKey(navName));
  console.log(`[moduleHasButtons] ${navName}: ${hasButtons}`);
  return hasButtons;
};

const getButtonType = (navName) => {
  const structureKey = getMatchingConfigKey(BUTTON_STRUCTURE, navName);
  const structure = BUTTON_STRUCTURE[structureKey];
  if (structure && typeof structure === 'object' && !Array.isArray(structure)) {
    return "nested";
  }
  return "simple";
};

const getButtonStructure = (navName) => {
  console.log(`[getButtonStructure] Getting structure for: ${navName}`);
  const structureKey = getMatchingConfigKey(BUTTON_STRUCTURE, navName);
  const structure = BUTTON_STRUCTURE[structureKey] || {};
  console.log(`[getButtonStructure] Structure:`, structure);
  return structure;
};

const hasOrderSubTabs = (apiTabName) => {
  return apiTabName === "appOrders" || apiTabName === "bulkOrders";
};

const Permission = () => {
  const theme = useTheme();
  const toast = useToast();
  const { setPageTitle } = usePageTitle();
  const { showDeleteConfirmation } = useDeleteConfirmation();

  // State
  const [userTypes, setUserTypes] = useState([]);
  const [loading, setLoading] = useState(false);
  const [permissionDialogOpen, setPermissionDialogOpen] = useState(false);
  const [userTypeDialogOpen, setUserTypeDialogOpen] = useState(false);
  const [editingId, setEditingId] = useState(null);
  const [saving, setSaving] = useState(false);
  const [deletingId, setDeletingId] = useState(null);
  
  const [navigationItems, setNavigationItems] = useState(() => {
    const loggedData = JSON.parse(localStorage.getItem("logged_data")) || {};
    const userRole = loggedData?.data?.user_type || '';
    const items = getAllNavigationItems(userRole, '0');
    const additionalItems = [
      { name: "Dashboard", permission: "dashboard", path: "/dashboard" },
      { name: "Farmer Management", permission: "farmerManagement", path: "/farmer-management" },
      { name: "Invoice", permission: "invoice", path: "/invoice" },  // Keep this as is
      { name: "Enquiry Form", permission: "enquiry", path: "/enquiry" },
      { name: "Category", permission: "category", path: "/category" },
      { name: "Disease Advisory", permission: "diseaseAdvisory", path: "/disease-advisory" },
      { name: "AI Chat Bot", permission: "aiChatBot", path: "/ai-chat-bot" },
      { name: "FAQ", permission: "FAQs", path: "/faq" },
      { name: "Product Management/Agri store", permission: "agriStore", path: "/dashboard/product-management" },  // Add if missing
      { name: "Translation Management/Content", permission: "groceries", path: "/dashboard/translation-management" }  // Add if missing
    ];
    const existingPermissions = items.map(i => i.permission);
    const newItems = additionalItems.filter(i => !existingPermissions.includes(i.permission));
    const allItems = [...items, ...newItems];
    console.log("[Navigation Items] Loaded:", allItems.map(i => i.name));
    return allItems;
  });
  
  const [navigationPermissions, setNavigationPermissions] = useState({});
  const [pagination, setPagination] = useState({
    current_page: 1,
    page_size: 10,
    total_items: 0,
    total_pages: 1,
  });
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);
  const pageSizeOptions = [10, 25, 50, 100];
  
  const [orderStatuses, setOrderStatuses] = useState({
    normal: [],
    bulk: []
  });
  const [permissionDialogDataLoading, setPermissionDialogDataLoading] = useState(false);
  const [expandedPermissions, setExpandedPermissions] = useState({});
  const [expandedSubTabs, setExpandedSubTabs] = useState({});

  const uniqueNavigationItems = useMemo(() => {
    const unique = [];
    const seenNames = new Set();
    navigationItems.forEach((nav) => {
      const nameLower = (nav.name || "").toLowerCase().trim();
      if (!nameLower) return;
      if (seenNames.has(nameLower)) return;
      seenNames.add(nameLower);
      unique.push(nav);
    });
    return unique;
  }, [navigationItems]);

  const [userTypeFormData, setUserTypeFormData] = useState({
    name: "",
    display_name: "",
    status: "Active",
  });

  // ============ PERMISSION FUNCTIONS ============
  const getCurrentButtonPermissions = () => {
    const moduleConfig = buttonConfig["User Types & Permissions"];
    console.log("Full Button Config:", buttonConfig);
    return moduleConfig ? moduleConfig : {};
  };
  
  const shouldEnableButton = (buttonKey) => {
    const permissions = getCurrentButtonPermissions();
    console.log(`[shouldEnableButton] Checking permission for: ${buttonKey} - Value: ${permissions[buttonKey]}`);
    return permissions[buttonKey] === true;
  };
  // ============ END PERMISSION FUNCTIONS ============

  const fetchOrderStatuses = useCallback(async () => {
    try {
      const normalResponse = await apiService.get(
        `${API_ENDPOINTS.GET_order_status_choices}?type=normal`
      );
      const bulkResponse = await apiService.get(
        `${API_ENDPOINTS.GET_order_status_choices}?type=bulk`
      );
      setOrderStatuses({
        normal: normalResponse?.data || [],
        bulk: bulkResponse?.data || []
      });
    } catch (error) {
      console.error("Error fetching order statuses:", error);
    }
  }, []);

  const fetchUserTypes = useCallback(async () => {
    setLoading(true);
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.Get_user_types}?page=${currentPage}&page_size=${pageSize}`
      );
      if (response?.success && response?.data) {
        setUserTypes(Array.isArray(response.data) ? response.data : []);
        setPagination({
          current_page: response.pagination?.current_page || 1,
          page_size: response.pagination?.page_size || pageSize,
          total_items: response.pagination?.total_items || 0,
          total_pages: response.pagination?.total_pages || 1,
        });
      } else {
        setUserTypes([]);
      }
    } catch (error) {
      console.error("Error fetching user types:", error);
      toast.showError("Failed to load user types");
    } finally {
      setLoading(false);
    }
  }, [currentPage, pageSize, toast]);

  // const fetchPermissions = useCallback(async (userTypeId) => {
  //   try {
  //     const response = await apiService.get(
  //       `${API_ENDPOINTS.Get_permissions}?user_type_id=${userTypeId}`
  //     );
      
  //     console.log("[fetchPermissions] API Response:", response);
      
  //     if (response?.success && response?.data) {
  //       const permissionsObj = response.data;
  //       const initialPermissions = {};
  //       console.log("[fetchPermissions] Processing permissions for navigation items...");
  //       console.log("[fetchPermissions] Navigation items:", navigationItems.map(i => i.name));
  //       navigationItems.forEach(nav => {
  //         const tabKey = getMatchingConfigKey(TAB_NAME_MAPPING, nav.name);
  //         const apiTabName = TAB_NAME_MAPPING[tabKey] || nav.permission;
  //         const permissionValue = permissionsObj[apiTabName];
          
  //         console.log(`[fetchPermissions] ${nav.name} (${apiTabName}):`, permissionValue);
          
  //         if (permissionValue !== undefined) {
  //           if (typeof permissionValue === 'object') {
  //             initialPermissions[nav.permission] = {
  //               allowed: permissionValue.allowed === true,
  //               sub_tabs: permissionValue.sub_tabs || {}
  //             };
  //           } else {
  //             initialPermissions[nav.permission] = {
  //               allowed: permissionValue === true,
  //               sub_tabs: {}
  //             };
  //           }
  //         } else {
  //           initialPermissions[nav.permission] = {
  //             allowed: false,
  //             sub_tabs: {}
  //           };
  //         }
  //       });
        
  //       console.log("[fetchPermissions] Initial permissions:", initialPermissions);
  //       setNavigationPermissions(initialPermissions);
  //     } else {
  //       const defaultPermissions = {};
  //       navigationItems.forEach(nav => {
  //         defaultPermissions[nav.permission] = { allowed: false, sub_tabs: {} };
  //       });
  //       setNavigationPermissions(defaultPermissions);
  //     }
  //   } catch (error) {
  //     console.error("Error fetching permissions:", error);
  //     const defaultPermissions = {};
  //     navigationItems.forEach(nav => {
  //       defaultPermissions[nav.permission] = { allowed: false, sub_tabs: {} };
  //     });
  //     setNavigationPermissions(defaultPermissions);
  //   }
  // }, [navigationItems]);

  const fetchPermissions = useCallback(async (userTypeId) => {
    try {
      const response = await apiService.get(
        `${API_ENDPOINTS.Get_permissions}?user_type_id=${userTypeId}`
      );

      console.log("[fetchPermissions] API Response:", response);

      if (response?.success && response?.data) {
        const permissionsObj = response.data;
        const initialPermissions = {};

        console.log("[fetchPermissions] Processing permissions for navigation items...");

        uniqueNavigationItems.forEach((nav) => {
          const tabKey = getMatchingConfigKey(TAB_NAME_MAPPING, nav.name);
          const apiTabName = TAB_NAME_MAPPING[tabKey] || nav.permission;
          const permissionValue = permissionsObj[apiTabName];

          console.log(`[fetchPermissions] ${nav.name} (${apiTabName}):`, permissionValue);

          if (permissionValue !== undefined) {
            if (typeof permissionValue === "object") {
              initialPermissions[nav.permission] = {
                allowed: permissionValue.allowed === true,
                sub_tabs: permissionValue.sub_tabs || {},
              };
            } else {
              initialPermissions[nav.permission] = {
                allowed: permissionValue === true,
                sub_tabs: {},
              };
            }
          } else {
            initialPermissions[nav.permission] = {
              allowed: false,
              sub_tabs: {},
            };
          }
        });

        console.log("[fetchPermissions] Initial permissions:", initialPermissions);
        setNavigationPermissions(initialPermissions);
      } else {
        const defaultPermissions = {};
        uniqueNavigationItems.forEach((nav) => {
          defaultPermissions[nav.permission] = { allowed: false, sub_tabs: {} };
        });
        setNavigationPermissions(defaultPermissions);
      }
    } catch (error) {
      console.error("Error fetching permissions:", error);
      const defaultPermissions = {};
      uniqueNavigationItems.forEach((nav) => {
        defaultPermissions[nav.permission] = { allowed: false, sub_tabs: {} };
      });
      setNavigationPermissions(defaultPermissions);
    }
  }, [uniqueNavigationItems]);

  useEffect(() => {
    setPageTitle("User Types & Permissions");
  }, [setPageTitle]);

  useEffect(() => {
    fetchUserTypes();
  }, [fetchUserTypes, currentPage, pageSize]);

  const handlePageChange = (_, value) => setCurrentPage(value);
  const handlePageSizeChange = (e) => {
    setPageSize(Number(e.target.value));
    setCurrentPage(1);
  };

  const handleOpenPermissionDialog = async (item) => {
    // Check permission for edit_permission
    if (!shouldEnableButton('edit_permission')) {
      toast.showError("You don't have permission to edit permissions");
      return;
    }
    
    if (item) {
      const userTypeId = item.id || item.user_type_id;
      console.log("[handleOpenPermissionDialog] Opening for user type ID:", userTypeId);
      setEditingId(userTypeId);
      setNavigationPermissions({});
      setPermissionDialogOpen(true);
      setPermissionDialogDataLoading(true);
      try {
        await fetchOrderStatuses();
        await fetchPermissions(userTypeId);
        setExpandedPermissions({});
        setExpandedSubTabs({});
      } finally {
        setPermissionDialogDataLoading(false);
      }
    }
  };

  const handleClosePermissionDialog = () => {
    setPermissionDialogOpen(false);
    setEditingId(null);
    setNavigationPermissions({});
    setExpandedPermissions({});
    setExpandedSubTabs({});
    setPermissionDialogDataLoading(false);
  };

  const handleOpenUserTypeDialog = (item = null) => {
    // Check permission for add_user_type or edit_user_type
    if (item) {
      if (!shouldEnableButton('edit_user_type')) {
        toast.showError("You don't have permission to edit user types");
        return;
      }
    } else {
      if (!shouldEnableButton('add_user_type')) {
        toast.showError("You don't have permission to add user types");
        return;
      }
    }
    
    if (item) {
      setEditingId(item.id || item.user_type_id);
      setUserTypeFormData({
        name: item.name || "",
        display_name: item.display_name || "",
        status: item.status || "Active",
      });
    } else {
      setEditingId(null);
      setUserTypeFormData({ name: "", display_name: "", status: "Active" });
    }
    setUserTypeDialogOpen(true);
  };

  const handleCloseUserTypeDialog = () => {
    setUserTypeDialogOpen(false);
    setEditingId(null);
    setUserTypeFormData({ name: "", display_name: "", status: "Active" });
  };

  const handlePermissionChange = (permission, allowed) => {
    console.log(`[handlePermissionChange] ${permission}: ${allowed}`);
    let subTabs = {};
    const navName = getNavNameByPermission(permission);
    const tabKey = getMatchingConfigKey(TAB_NAME_MAPPING, navName);
    const apiTabName = TAB_NAME_MAPPING[tabKey] || permission;
    
    if (allowed === true && hasOrderSubTabs(apiTabName)) {
      const statusList = apiTabName === "appOrders" ? orderStatuses.normal : orderStatuses.bulk;
      statusList.forEach(status => {
        subTabs[status.name.toLowerCase()] = true;
      });
    }

    setNavigationPermissions(prev => ({
      ...prev,
      [permission]: { allowed, sub_tabs: subTabs }
    }));
  };

  const getNavNameByPermission = (permission) => {
    const nav = navigationItems.find(n => n.permission === permission);
    return nav ? nav.name : "";
  };

  const handleSubTabChange = (permission, subTabKey, checked) => {
    console.log(`[handleSubTabChange] ${permission}: ${subTabKey} = ${checked}`);
    setNavigationPermissions(prev => {
      const currentSubTabs = prev[permission]?.sub_tabs || {};
      const newSubTabs = { ...currentSubTabs };
      if (checked) newSubTabs[subTabKey] = true;
      else delete newSubTabs[subTabKey];
      return {
        ...prev,
        [permission]: { ...prev[permission], sub_tabs: newSubTabs }
      };
    });
  };

  const handleSelectAllStatuses = (permission) => {
    const navName = getNavNameByPermission(permission);
    const tabKey = getMatchingConfigKey(TAB_NAME_MAPPING, navName);
    const apiTabName = TAB_NAME_MAPPING[tabKey] || permission;
    const statusList = apiTabName === "appOrders" ? orderStatuses.normal : orderStatuses.bulk;
    const subTabs = {};
    statusList.forEach(status => {
      subTabs[status.name.toLowerCase()] = true;
    });
    setNavigationPermissions(prev => ({
      ...prev,
      [permission]: { ...prev[permission], sub_tabs: subTabs }
    }));
  };

  const handleDeselectAllStatuses = (permission) => {
    setNavigationPermissions(prev => ({
      ...prev,
      [permission]: { ...prev[permission], sub_tabs: {} }
    }));
  };

  const handleSelectAllButtons = (permission, buttonCategory, buttons) => {
    console.log(`[handleSelectAllButtons] ${permission} - ${buttonCategory}`);
    setNavigationPermissions(prev => {
      const currentSubTabs = prev[permission]?.sub_tabs || {};
      const newSubTabs = { ...currentSubTabs };
      const buttonKeys = Array.isArray(buttons) ? buttons : Object.keys(buttons);
      buttonKeys.forEach(buttonKey => {
        const subTabKey = buttonCategory ? `${buttonCategory}_${buttonKey}` : buttonKey;
        newSubTabs[subTabKey] = true;
      });
      return {
        ...prev,
        [permission]: { ...prev[permission], sub_tabs: newSubTabs }
      };
    });
  };

  const handleDeselectAllButtons = (permission, buttonCategory, buttons) => {
    console.log(`[handleDeselectAllButtons] ${permission} - ${buttonCategory}`);
    setNavigationPermissions(prev => {
      const currentSubTabs = prev[permission]?.sub_tabs || {};
      const newSubTabs = { ...currentSubTabs };
      const buttonKeys = Array.isArray(buttons) ? buttons : Object.keys(buttons);
      buttonKeys.forEach(buttonKey => {
        const subTabKey = buttonCategory ? `${buttonCategory}_${buttonKey}` : buttonKey;
        delete newSubTabs[subTabKey];
      });
      return {
        ...prev,
        [permission]: { ...prev[permission], sub_tabs: newSubTabs }
      };
    });
  };

  const toggleExpand = (permission) => {
    setExpandedPermissions(prev => ({ ...prev, [permission]: !prev[permission] }));
  };

  const toggleSubTabExpand = (permission, subTabKey) => {
    setExpandedSubTabs(prev => ({
      ...prev,
      [`${permission}_${subTabKey}`]: !prev[`${permission}_${subTabKey}`]
    }));
  };

  const handleSaveUserType = async () => {
    // Check permission for add_user_type or edit_user_type
    if (editingId) {
      if (!shouldEnableButton('edit_user_type')) {
        toast.showError("You don't have permission to edit user types");
        return;
      }
    } else {
      if (!shouldEnableButton('add_user_type')) {
        toast.showError("You don't have permission to add user types");
        return;
      }
    }

    if (!userTypeFormData.name?.trim() || !userTypeFormData.display_name?.trim()) {
      toast.showError("Please fill all required fields");
      return;
    }

    setSaving(true);
    try {
      const payload = {
        name: userTypeFormData.name.trim(),
        display_name: userTypeFormData.display_name.trim(),
        status: userTypeFormData.status,
      };
      if (editingId) payload.user_type_id = editingId;

      const response = editingId
        ? await apiService.put(API_ENDPOINTS.Update_user_type, payload)
        : await apiService.post(API_ENDPOINTS.Add_user_type, payload);

      if (response?.success) {
        toast.showSuccess(editingId ? "User type updated successfully" : "User type added successfully");
        handleCloseUserTypeDialog();
        fetchUserTypes();
      } else {
        toast.showError(response?.message || "Failed to save user type");
      }
    } catch (error) {
      console.error("Error saving user type:", error);
      toast.showError(error?.message || "Failed to save user type");
    } finally {
      setSaving(false);
    }
  };

  const handleSavePermissions = async () => {
    // Check permission for edit_permission
    if (!shouldEnableButton('edit_permission')) {
      toast.showError("You don't have permission to edit permissions");
      return;
    }

    if (!editingId) {
      toast.showError("User type ID is required");
      return;
    }

    setSaving(true);
    try {
      const permissionsObj = {};
      const authUser = JSON.parse(localStorage.getItem("logged_data"))?.data?.user_id || "1";
      
      uniqueNavigationItems.forEach(nav => {
        const permissionData = navigationPermissions[nav.permission];
        const tabKey = getMatchingConfigKey(TAB_NAME_MAPPING, nav.name);
        const apiTabName = TAB_NAME_MAPPING[tabKey] || nav.permission;
        
        if (permissionData) {
          const isOrderPermission = hasOrderSubTabs(apiTabName);
          
          if (isOrderPermission) {
            if (permissionData.allowed === true) {
              permissionsObj[apiTabName] = permissionData.sub_tabs && Object.keys(permissionData.sub_tabs).length > 0
                ? { allowed: true, sub_tabs: permissionData.sub_tabs }
                : true;
            } else {
              permissionsObj[apiTabName] = false;
            }
          } else {
            if (permissionData.allowed === true) {
              permissionsObj[apiTabName] = permissionData.sub_tabs && Object.keys(permissionData.sub_tabs).length > 0
                ? { allowed: true, sub_tabs: permissionData.sub_tabs }
                : true;
            } else {
              permissionsObj[apiTabName] = false;
            }
          }
        } else {
          permissionsObj[apiTabName] = false;
        }
      });

      const payload = { user_type_id: editingId, permissions: permissionsObj, auth_user: authUser };
      console.log("[handleSavePermissions] Payload:", payload);
      const response = await apiService.post(API_ENDPOINTS.Add_permission, payload);

      if (response?.success) {
        toast.showSuccess("Permissions saved successfully");
        handleClosePermissionDialog();
        fetchUserTypes();
      } else {
        toast.showError(response?.message || "Failed to save permissions");
      }
    } catch (error) {
      console.error("Error saving permissions:", error);
      toast.showError("Failed to save permissions");
    } finally {
      setSaving(false);
    }
  };

  const handleDeleteUserType = async (id) => {
    // Check permission for delete
    if (!shouldEnableButton('delete_user_type')) {
      toast.showError("You don't have permission to delete user types");
      return;
    }

    const confirmed = await showDeleteConfirmation({
      title: "Delete User Type",
      message: "Are you sure you want to delete this user type?",
    });
    if (!confirmed) return;
    
    setDeletingId(id);
    try {
      const response = await apiService.delete(`${API_ENDPOINTS.Delete_user_type}?user_type_id=${id}`);
      if (response?.success) {
        toast.showSuccess("User type deleted successfully");
        fetchUserTypes();
      } else {
        toast.showError(response?.message || "Failed to delete user type");
      }
    } catch (error) {
      console.error("Error deleting user type:", error);
      toast.showError(error?.message || "Failed to delete user type");
    } finally {
      setDeletingId(null);
    }
  };

  // Render nested button permissions
  const renderButtonCategory = (nav, buttonCategory, buttons, isExpanded, onToggleExpand) => {
    const permissionData = navigationPermissions[nav.permission];
    const buttonKeys = Array.isArray(buttons) ? buttons : Object.keys(buttons);
    const isForcedDisabledCategory = nav?.name === "Agronomist" && buttonCategory === "Connected Button";
    const selectedCount = Object.keys(permissionData?.sub_tabs || {})
      .filter(key => key.startsWith(`${buttonCategory}_`))
      .filter(key => permissionData.sub_tabs[key] === true).length;
    const totalCount = buttonKeys.length;

    console.log(`[renderButtonCategory] ${nav.name} - ${buttonCategory}: selected=${selectedCount}, total=${totalCount}`);

    if (totalCount === 0) return null;

    return (
      <Box
        key={`${nav.permission}_${buttonCategory}`}
        sx={{
          mb: 1.25,
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          borderRadius: 1.5,
          bgcolor: "background.paper",
          px: 1.25,
          py: 1,
          opacity: isForcedDisabledCategory ? 0.7 : 1,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
          <Box>
            <Typography variant="subtitle2" color="text.primary" fontWeight={600}>{buttonCategory}</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {nav.name} {" > "} {buttonCategory} {" > "} Button Permissions
            </Typography>
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Button
              size="small"
              variant="contained"
              sx={{ textTransform: "none", minWidth: 86 }}
              onClick={() => handleSelectAllButtons(nav.permission, buttonCategory, buttons)}
              disabled={isForcedDisabledCategory}
            >
              Select all
            </Button>
            <Button
              size="small"
              variant="outlined"
              color="inherit"
              sx={{ textTransform: "none", minWidth: 94 }}
              onClick={() => handleDeselectAllButtons(nav.permission, buttonCategory, buttons)}
              disabled={isForcedDisabledCategory}
            >
              Deselect all
            </Button>
            <IconButton size="small" onClick={onToggleExpand} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Stack>
        </Stack>

        {selectedCount > 0 && !isExpanded && (
          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {buttonKeys.filter(key => permissionData?.sub_tabs?.[`${buttonCategory}_${key}`] === true).slice(0, 3).map(key => (
              <Chip key={key} label={key.replace(/_/g, ' ')} size="small" color="primary" variant="outlined" sx={{ height: 24, fontSize: "0.7rem" }} />
            ))}
            {selectedCount > 3 && <Typography variant="caption" color="text.secondary">+{selectedCount - 3} more</Typography>}
          </Box>
        )}

        <Collapse in={isExpanded}>
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block", px: 0.5 }}>
              Choose actions available in this status
            </Typography>
            <Grid container spacing={0.5}>
              {buttonKeys.map(buttonKey => {
                const subTabKey = `${buttonCategory}_${buttonKey}`;
                return (
                  <Grid item xs={12} sm={6} md={4} key={buttonKey}>
                    <FormControlLabel
                      sx={{ m: 0, px: 0.5 }}
                      control={
                        <Checkbox
                          checked={permissionData?.sub_tabs?.[subTabKey] === true}
                          onChange={(e) => handleSubTabChange(nav.permission, subTabKey, e.target.checked)}
                          size="small"
                          disabled={!permissionData?.allowed || isForcedDisabledCategory}
                        />
                      }
                      label={<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{buttonKey.replace(/_/g, ' ')}</Typography>}
                    />
                  </Grid>
                );
              })}
            </Grid>
          </Box>
        </Collapse>
      </Box>
    );
  };

  // Render simple button permissions
  const renderSimpleButtons = (nav, buttons, isExpanded, onToggleExpand) => {
    const permissionData = navigationPermissions[nav.permission];
    const buttonKeys = Array.isArray(buttons) ? buttons : Object.keys(buttons);
    const selectedCount = Object.keys(permissionData?.sub_tabs || {}).filter(key => permissionData.sub_tabs[key] === true).length;
    const totalCount = buttonKeys.length;

    console.log(`[renderSimpleButtons] ${nav.name}: selected=${selectedCount}, total=${totalCount}, buttons:`, buttonKeys);

    if (totalCount === 0) return null;

    return (
      <Box
        key={`${nav.permission}_simple_buttons`}
        sx={{
          mb: 1.25,
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          borderRadius: 1.5,
          bgcolor: "background.paper",
          px: 1.25,
          py: 1,
        }}
      >
        <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1}>
          <Box>
            <Typography variant="subtitle2" color="text.primary" fontWeight={600}>Actions</Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
              {nav.name} {" > "} Actions {" > "} Button Permissions
            </Typography>
            <Typography variant="caption" color="text.secondary">Selected: {selectedCount} of {totalCount}</Typography>
          </Box>
          <Stack direction="row" spacing={0.75} alignItems="center">
            <Button size="small" variant="contained" sx={{ textTransform: "none", minWidth: 86 }} onClick={() => {
              const newSubTabs = {};
              buttonKeys.forEach(key => { newSubTabs[key] = true; });
              setNavigationPermissions(prev => ({ ...prev, [nav.permission]: { ...prev[nav.permission], sub_tabs: newSubTabs } }));
            }}>Select all</Button>
            <Button size="small" variant="outlined" color="inherit" sx={{ textTransform: "none", minWidth: 94 }} onClick={() => {
              setNavigationPermissions(prev => ({ ...prev, [nav.permission]: { ...prev[nav.permission], sub_tabs: {} } }));
            }}>Deselect all</Button>
            <IconButton size="small" onClick={onToggleExpand} sx={{ border: `1px solid ${theme.palette.divider}`, borderRadius: 1 }}>
              {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
            </IconButton>
          </Stack>
        </Stack>

        {selectedCount > 0 && !isExpanded && (
          <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.5 }}>
            {buttonKeys.filter(key => permissionData?.sub_tabs?.[key] === true).slice(0, 3).map(key => (
              <Chip key={key} label={key.replace(/_/g, ' ')} size="small" color="primary" variant="outlined" sx={{ height: 24, fontSize: "0.7rem" }} />
            ))}
            {selectedCount > 3 && <Typography variant="caption" color="text.secondary">+{selectedCount - 3} more</Typography>}
          </Box>
        )}

        <Collapse in={isExpanded}>
          <Box sx={{ mt: 1 }}>
            <Divider sx={{ mb: 1 }} />
            <Typography variant="caption" color="text.secondary" sx={{ mb: 0.5, display: "block", px: 0.5 }}>
              Choose actions available in this module
            </Typography>
            <Grid container spacing={0.5}>
              {buttonKeys.map(buttonKey => (
                <Grid item xs={12} sm={6} md={4} key={buttonKey}>
                  <FormControlLabel
                    sx={{ m: 0, px: 0.5 }}
                    control={
                      <Checkbox
                        checked={permissionData?.sub_tabs?.[buttonKey] === true}
                        onChange={(e) => handleSubTabChange(nav.permission, buttonKey, e.target.checked)}
                        size="small"
                        disabled={!permissionData?.allowed}
                      />
                    }
                    label={<Typography variant="body2" sx={{ textTransform: 'capitalize' }}>{buttonKey.replace(/_/g, ' ')}</Typography>}
                  />
                </Grid>
              ))}
            </Grid>
          </Box>
        </Collapse>
      </Box>
    );
  };

  // Render permission row
  const renderPermissionRow = (nav) => {
    const tabKey = getMatchingConfigKey(TAB_NAME_MAPPING, nav.name);
    const apiTabName = TAB_NAME_MAPPING[tabKey] || nav.permission;
    const isOrderPermission = hasOrderSubTabs(apiTabName);
    const permissionData = navigationPermissions[nav.permission];
    const isAllowed = permissionData?.allowed === true;
    const isExpanded = expandedPermissions[nav.permission];
    
    const statusList = isOrderPermission ? (apiTabName === "appOrders" ? orderStatuses.normal : orderStatuses.bulk) : [];
    const selectedCount = Object.keys(permissionData?.sub_tabs || {}).length;
    const totalCount = statusList.length;
    const showStatusSection = isOrderPermission && statusList.length > 0;
    
    const hasButtons = moduleHasButtons(nav.name);
    const buttonStructure = getButtonStructure(nav.name);
    const hasNestedButtons = hasButtons && getButtonType(nav.name) === "nested";
    const hasSimpleButtons = hasButtons && getButtonType(nav.name) === "simple";
    const hasAnyButtons = hasNestedButtons || hasSimpleButtons;
    const showExpandButton = showStatusSection || hasAnyButtons;

    console.log(`[renderPermissionRow] ${nav.name}: hasButtons=${hasButtons}, hasAnyButtons=${hasAnyButtons}, hasNested=${hasNestedButtons}, hasSimple=${hasSimpleButtons}`);

    return (
      <Box
        key={`nav_${nav.permission}`}
        sx={{
          mb: 1.25,
          border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
          borderRadius: 2,
          bgcolor: "background.paper",
          overflow: "hidden",
        }}
      >
        <Box sx={{ py: 1.25, px: 1.5 }}>
          <Stack direction="row" justifyContent="space-between" alignItems="center" spacing={1} flexWrap="wrap">
            <Typography variant="body1" fontWeight={700}>{nav.name}</Typography>
            <Stack direction="row" alignItems="center" spacing={0.5}>
              <Switch checked={isAllowed} onChange={(e) => handlePermissionChange(nav.permission, e.target.checked)} color="primary" />
              {showExpandButton && (
                <IconButton
                  size="small"
                  onClick={() => toggleExpand(nav.permission)}
                  sx={{
                    border: `1px solid ${theme.palette.divider}`,
                    borderRadius: 1,
                    p: 0.25,
                  }}
                >
                  {isExpanded ? <ExpandLess fontSize="small" /> : <ExpandMore fontSize="small" />}
                </IconButton>
              )}
            </Stack>
          </Stack>

          {showStatusSection && !isExpanded && selectedCount > 0 && (
            <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              {statusList.filter(status => permissionData?.sub_tabs?.[status.name.toLowerCase()] === true).slice(0, 4).map(status => (
                <Chip key={status.id} label={status.name} size="small" color="primary" variant="outlined" sx={{ height: 24, fontSize: "0.72rem" }} />
              ))}
              {selectedCount > 4 && <Typography variant="caption" color="text.secondary">+{selectedCount - 4} more</Typography>}
            </Box>
          )}

          {hasAnyButtons && !isExpanded && permissionData?.sub_tabs && Object.keys(permissionData.sub_tabs).length > 0 && (
            <Box sx={{ mt: 1, display: "flex", flexWrap: "wrap", gap: 0.75 }}>
              <Chip label={`${Object.keys(permissionData.sub_tabs).length} button permissions selected`} size="small" variant="outlined" sx={{ height: 24 }} />
            </Box>
          )}

          {isExpanded && showExpandButton && (
            <Collapse in={isExpanded}>
              <Box sx={{ mt: 1.25, p: 1.5, borderRadius: 1.5, bgcolor: alpha(theme.palette.primary.main, 0.04) }}>
                <Box
                  sx={{
                    mb: 1.25,
                    p: 1,
                    borderRadius: 1,
                    border: `1px solid ${alpha(theme.palette.primary.main, 0.18)}`,
                    bgcolor: alpha(theme.palette.primary.main, 0.06),
                  }}
                >
                  <Typography variant="caption" color="text.secondary" sx={{ display: "block" }}>
                    You are configuring
                  </Typography>
                  <Typography variant="subtitle2" fontWeight={700}>
                    {nav.name}
                  </Typography>
                </Box>
                {showStatusSection && (
                  <Box sx={{ mb: 1.5 }}>
                    <Stack direction={{ xs: "column", sm: "row" }} justifyContent="space-between" alignItems={{ xs: "flex-start", sm: "center" }} spacing={1} sx={{ mb: 1 }}>
                      <Typography variant="subtitle2" color="primary" fontWeight={700}>Step 1: Select Status Permissions</Typography>
                      <Stack direction="row" spacing={1}>
                        <Button
                          size="small"
                          variant="contained"
                          onClick={() => handleSelectAllStatuses(nav.permission)}
                          sx={{ textTransform: "none", minWidth: 94 }}
                        >
                          Select all
                        </Button>
                        <Button
                          size="small"
                          variant="outlined"
                          color="inherit"
                          onClick={() => handleDeselectAllStatuses(nav.permission)}
                          sx={{ textTransform: "none", minWidth: 102 }}
                        >
                          Deselect all
                        </Button>
                      </Stack>
                    </Stack>
                    <Grid container spacing={1}>
                      {statusList.map(status => (
                        <Grid item xs={12} sm={6} md={4} lg={3} key={status.id}>
                          <Box
                            sx={{
                              border: `1px solid ${alpha(theme.palette.divider, 0.8)}`,
                              borderRadius: 1,
                              px: 1,
                              py: 0.4,
                              height: "100%",
                              bgcolor: "background.paper",
                              display: "flex",
                              alignItems: "center",
                              justifyContent: "space-between",
                            }}
                          >
                            <Typography variant="body2" fontWeight={500}>{status.name}</Typography>
                            <Checkbox
                              checked={permissionData?.sub_tabs?.[status.name.toLowerCase()] === true}
                              onChange={(e) => handleSubTabChange(nav.permission, status.name.toLowerCase(), e.target.checked)}
                              size="small"
                              sx={{ p: 0.5 }}
                            />
                          </Box>
                        </Grid>
                      ))}
                    </Grid>
                  </Box>
                )}

                {hasAnyButtons && (
                  <Box sx={{ mt: showStatusSection ? 1.5 : 0 }}>
                    <Typography variant="subtitle2" color="primary" fontWeight={700} sx={{ mb: 1 }}>
                      {showStatusSection ? "Step 2: Configure Button Permissions" : "Configure Button Permissions"}
                    </Typography>
                    {hasNestedButtons ? (
                      Object.entries(buttonStructure)
                        .filter(([_, buttons]) => buttons && (Array.isArray(buttons) ? buttons.length > 0 : Object.keys(buttons).length > 0))
                        .map(([buttonCategory, buttons]) => {
                          const isExpandedSub = expandedSubTabs[`${nav.permission}_${buttonCategory}`];
                          return renderButtonCategory(nav, buttonCategory, buttons, isExpandedSub, () => toggleSubTabExpand(nav.permission, buttonCategory));
                        })
                    ) : hasSimpleButtons && buttonStructure && (Array.isArray(buttonStructure) ? buttonStructure.length > 0 : Object.keys(buttonStructure).length > 0) ? (
                      (() => {
                        const isExpandedSub = expandedSubTabs[`${nav.permission}_simple`];
                        return renderSimpleButtons(nav, buttonStructure, isExpandedSub, () => toggleSubTabExpand(nav.permission, "simple"));
                      })()
                    ) : null}
                  </Box>
                )}
              </Box>
            </Collapse>
          )}
        </Box>
      </Box>
    );
  };

  return (
    <PageContainer>
      <HeaderCard>
        <Box>
          <Typography variant="h6" fontWeight={700}>User Types & Permissions</Typography>
          <Muted variant="body2">Manage user types and their permissions</Muted>
        </Box>
        <Box sx={{ display: "flex", alignItems: "center", gap: 2, flexWrap: "wrap" }}>
          <FormControl size="small" sx={{ minWidth: 110 }}>
            <InputLabel>Rows</InputLabel>
            <Select value={pageSize} label="Rows" onChange={handlePageSizeChange}>
              {pageSizeOptions.map(opt => <MenuItem key={opt} value={opt}>{opt}</MenuItem>)}
            </Select>
          </FormControl>
          <Tooltip title={!shouldEnableButton('add_user_type') ? "You don't have permission to add user types" : "Add User Type"}>
            <span>
              <Button 
                startIcon={<Add />} 
                variant="contained" 
                onClick={() => handleOpenUserTypeDialog()} 
                disabled={!shouldEnableButton('add_user_type')}
                sx={{ opacity: !shouldEnableButton('add_user_type') ? 0.6 : 1 }}
              >
                Add User Type
              </Button>
            </span>
          </Tooltip>
          <Button startIcon={<Refresh />} variant="outlined" onClick={fetchUserTypes} disabled={loading}>
            {loading ? "Refreshing..." : "Refresh"}
          </Button>
        </Box>
      </HeaderCard>

      <TableCard>
        <TableContainer component={Paper} sx={{ boxShadow: 3, borderRadius: 2, maxHeight: 520, overflow: "auto" }}>
          <Table stickyHeader size="small">
            <TableHead sx={{ position: "sticky", top: 0, zIndex: 2 }}>
              <TableRow>
                <StyledTableCell className="header" align="center" sx={{ width: 72 }}>Sr No</StyledTableCell>
                <StyledTableCell className="header">Name</StyledTableCell>
                <StyledTableCell className="header">Display name</StyledTableCell>
                <StyledTableCell className="header" align="center">Status</StyledTableCell>
                <StyledTableCell className="header" align="center" sx={{ minWidth: 220 }}>Actions</StyledTableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 5 }}><CircularProgress size={28} /><Typography variant="body2" sx={{ mt: 1 }}>Loading user types…</Typography></TableCell></TableRow>
              ) : userTypes.length === 0 ? (
                <TableRow><TableCell colSpan={5} align="center" sx={{ py: 4 }}><Muted variant="body2">No user types found.</Muted></TableCell></TableRow>
              ) : (
                userTypes.map((item, index) => (
                  <StyledTableRow key={item.id || item.user_type_id}>
                    <StyledTableCell align="center"><Typography variant="body2" color="text.secondary" fontWeight={500}>{(pagination.current_page - 1) * pagination.page_size + index + 1}</Typography></StyledTableCell>
                    <StyledTableCell><Typography variant="body2" fontWeight={600}>{item.name || "N/A"}</Typography></StyledTableCell>
                    <StyledTableCell><Typography variant="body2">{item.display_name || "N/A"}</Typography></StyledTableCell>
                    <StyledTableCell align="center"><Chip label={item.status || "Active"} size="small" color={item.status === "Active" ? "success" : "default"} variant="outlined" /></StyledTableCell>
                    <StyledTableCell align="center">
                      <Box sx={{ display: "flex", gap: 1, justifyContent: "center", alignItems: "center", flexWrap: "wrap" }}>
                        <Tooltip title={!shouldEnableButton('edit_permission') ? "You don't have permission to edit permissions" : "Edit Permissions"}>
                          <span>
                            <Button 
                              size="small" 
                              variant="contained" 
                              color="primary" 
                              onClick={() => handleOpenPermissionDialog(item)}
                              disabled={!shouldEnableButton('edit_permission')}
                              sx={{ opacity: !shouldEnableButton('edit_permission') ? 0.6 : 1 }}
                            >
                              Permissions
                            </Button>
                          </span>
                        </Tooltip>
                        <Tooltip title={!shouldEnableButton('edit_user_type') ? "You don't have permission to edit user types" : "Edit User Type"}>
                          <span>
                            <IconButton 
                              color="primary" 
                              size="small" 
                              onClick={() => handleOpenUserTypeDialog(item)}
                              disabled={!shouldEnableButton('edit_user_type')}
                              sx={{ opacity: !shouldEnableButton('edit_user_type') ? 0.5 : 1 }}
                            >
                              <Edit fontSize="small" />
                            </IconButton>
                          </span>
                        </Tooltip>
                        <Tooltip title={!shouldEnableButton('delete_user_type') ? "You don't have permission to delete user types" : "Delete User Type"}>
                          <span>
                            <IconButton 
                              color="error" 
                              size="small" 
                              onClick={() => handleDeleteUserType(item.id || item.user_type_id)} 
                              disabled={deletingId === (item.id || item.user_type_id) || !shouldEnableButton('delete_user_type')}
                              sx={{ opacity: !shouldEnableButton('delete_user_type') ? 0.5 : 1 }}
                            >
                              {deletingId === (item.id || item.user_type_id) ? <CircularProgress size={18} /> : <Delete fontSize="small" />}
                            </IconButton>
                          </span>
                        </Tooltip>
                      </Box>
                    </StyledTableCell>
                  </StyledTableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
      </TableCard>

      <AppPaginationBar pagination={pagination} pageSize={pageSize} onPageChange={handlePageChange} itemLabel="user types" />

      {/* User Type Dialog */}
      <Dialog open={userTypeDialogOpen} onClose={handleCloseUserTypeDialog} maxWidth="sm" fullWidth>
        <DialogTitle>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6">{editingId ? "Edit User Type" : "Add User Type"}</Typography>
            <IconButton onClick={handleCloseUserTypeDialog} size="small"><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers>
          <Box sx={{ display: "flex", flexDirection: "column", gap: 2, py: 1 }}>
            <TextField 
              label="Name *" 
              value={userTypeFormData.name} 
              onChange={(e) => setUserTypeFormData(prev => ({ ...prev, name: e.target.value }))} 
              fullWidth 
              size="small" 
              disabled={saving}
            />
            <TextField 
              label="Display Name *" 
              value={userTypeFormData.display_name} 
              onChange={(e) => setUserTypeFormData(prev => ({ ...prev, display_name: e.target.value }))} 
              fullWidth 
              size="small" 
              disabled={saving}
            />
            <FormControl fullWidth size="small">
              <InputLabel>Status</InputLabel>
              <Select 
                value={userTypeFormData.status} 
                label="Status" 
                onChange={(e) => setUserTypeFormData(prev => ({ ...prev, status: e.target.value }))}
                disabled={saving}
              >
                <MenuItem value="Active">Active</MenuItem>
                <MenuItem value="Inactive">Inactive</MenuItem>
              </Select>
            </FormControl>
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseUserTypeDialog} disabled={saving}>Cancel</Button>
          <Button 
            onClick={handleSaveUserType} 
            variant="contained" 
            color="primary" 
            disabled={saving || !userTypeFormData.name?.trim() || !userTypeFormData.display_name?.trim()}
          >
            {saving ? <CircularProgress size={20} /> : (editingId ? "Update" : "Add")}
          </Button>
        </DialogActions>
      </Dialog>

      {/* Permissions Dialog */}
      <Dialog
        open={permissionDialogOpen}
        onClose={handleClosePermissionDialog}
        maxWidth="lg"
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: 2,
            minHeight: { md: "80vh" },
          }
        }}
      >
        <DialogTitle sx={{ pb: 1.25 }}>
          <Box sx={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
            <Typography variant="h6" fontWeight={700}>Manage Permissions</Typography>
            <IconButton onClick={handleClosePermissionDialog} size="small" disabled={saving}><Close /></IconButton>
          </Box>
        </DialogTitle>
        <DialogContent dividers sx={{ px: { xs: 1.5, sm: 2.5 }, py: 1.5 }}>
          {permissionDialogDataLoading ? <Box sx={{ display: "flex", justifyContent: "center", py: 4 }}><CircularProgress /></Box> : (
            <Paper variant="outlined" sx={{ p: { xs: 1, sm: 1.5 }, borderRadius: 2, bgcolor: "background.default" }}>
              <Muted variant="body2" sx={{ display: "block", mb: 2 }}>💡 Toggle modules to enable/disable access. Expand modules to configure specific permissions.</Muted>

              {uniqueNavigationItems.map((nav) => renderPermissionRow(nav))}
            </Paper>
          )}
        </DialogContent>
        <DialogActions sx={{ px: 3, py: 2 }}>
          <Button onClick={handleClosePermissionDialog} disabled={saving}>Cancel</Button>
          <Tooltip title={!shouldEnableButton('edit_permission') ? "You don't have permission to save permissions" : ""}>
            <span>
              <Button 
                onClick={handleSavePermissions} 
                variant="contained" 
                color="primary" 
                disabled={saving || !shouldEnableButton('edit_permission')}
              >
                {saving ? <CircularProgress size={20} /> : "Save Permissions"}
              </Button>
            </span>
          </Tooltip>
        </DialogActions>
      </Dialog>
    </PageContainer>
  );
};

export default Permission;